import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { BookOpen, ArrowRight, Flame, Layers, ShirtIcon, Wand2, Palette } from "lucide-react";

const ARTICLES = [
  { slug: "underbase-101", title: "Underbase 101 for DTF & DTG", tag: "Prepress", icon: Layers,
    excerpt: "The white underbase is what makes color prints pop on dark shirts. Here’s exactly how to build one that doesn’t crack, flood, or halo." },
  { slug: "lpi-cheatsheet", title: "LPI cheatsheet by fabric", tag: "Prepress", icon: Palette,
    excerpt: "Cotton, poly, tri-blend, waffle knit. Every fabric wants a different LPI. Save this cheat sheet for your studio wall." },
  { slug: "which-halftone-style", title: "Which of the 12 halftone styles for which artwork", tag: "Design", icon: Wand2,
    excerpt: "Round dots for photos. Diamonds for grunge. FM stochastic for detail work. Here’s how to pick." },
  { slug: "soft-hand-secrets", title: "The 5 things that make a DTF print feel soft", tag: "Quality", icon: ShirtIcon,
    excerpt: "It’s not just ink density. Fabric prep, cure temp, and pattern choice all stack." },
];

const ARTICLE_BODY = {
  "underbase-101": `
The white underbase is a full-strength white plate that sits below your color print. Without it, colors sink into dark garments and look muted. Get it right and prints feel like they float on top of the fabric.

### When you need an underbase
- Any dark-shirt print (charcoal, navy, black)
- Any color that has to survive a busy background
- Prints that need to hold detail after wash

### The three underbase mistakes we see most
1. **Full-strength everywhere.** Kills the hand feel. Choke your underbase to 90-95% and let it breathe.
2. **No choke.** Colors halo around the edges because the underbase is a hair wider than the print. Furnace does the choke automatically.
3. **One-size-fits-all opacity.** For gradients, use a dot pattern in the underbase too so the fade doesn’t look chunky.

### Furnace default underbase settings
- Choke: 1 px at 300 DPI (adjustable in Halftone Studio)
- Fabric: dark auto-enables underbase
- Style: matches your top halftone by default so colors align

> Rule of thumb: if the underbase looks slightly smaller than the color layer when you preview, you nailed it.
`,
  "lpi-cheatsheet": `
LPI (lines per inch) controls how many halftone dots pack into an inch of print. Higher LPI = more detail but softer edges; lower LPI = punchier but chunkier.

### Suggested defaults by fabric
| Fabric              | LPI  | Angle    | Notes |
|---------------------|------|----------|-------|
| 100% cotton         | 32   | 22.5°    | Sweet spot for most DTF |
| 50/50 poly-cotton   | 28   | 22.5°    | Slightly softer, more forgiving |
| Tri-blend           | 22   | 30°      | Loose weave, lower LPI holds up |
| Waffle knit         | 18   | 45°      | Heavy texture, big dots |
| Ringspun combed     | 40   | 22.5°    | Smooth surface can take detail |
| 100% polyester      | 35   | 22.5°    | Dye migration risk — keep coverage low |
| Fleece / hoodies    | 20   | 30°      | Absorbent, needs bold pattern |

### Tips
- If your print is losing detail, bump LPI up 5-10.
- If you see moiré (weird interference patterns), change the angle by 15°.
- For gradient-heavy artwork try FM stochastic — it has no fixed grid so moiré disappears.
`,
  "which-halftone-style": `
All 12 halftone styles produce a press-ready print, but each one has a look.

### The 12 patterns, quick guide
- **AM Round** — photorealism, portraits, skin tones. Default for a reason.
- **Square** — bold graphic tees, retro pixel-print vibe.
- **Diamond** — vintage / grunge, works great in single-color prints.
- **Ellipse** — flesh tones and midtones; less banding than round.
- **Line H / Line 45°** — editorial, poster-style, high contrast.
- **Cross** — texture on top of solid shapes, adds depth without noise.
- **Hex** — modern, futuristic; a favorite for streetwear.
- **Concentric** — ring-based tone; excellent for logos.
- **Wave** — organic feel, great for surf / skate art.
- **FM Stochastic** — photographic detail, kills moiré.
- **Newsprint** — chunky, retro, huge visual impact from a distance.

### How to pick
- Photo of a person? AM Round or Ellipse.
- Bold graphic? Square, Diamond, or Newsprint.
- Fine detail on a printed sample and you’re seeing moiré? FM Stochastic.
- Trying to look editorial / “The New Yorker”? Line 45°.
`,
  "soft-hand-secrets": `
Softness is what makes customers reorder. It’s the difference between a shirt they wear once and one they live in.

### Five levers that stack
1. **Halftone the fills.** Solid ink = plastic feel. Even 90% coverage with dots feels lighter.
2. **Choke the underbase.** 1-2 px choke removes the stiff white ring you can feel on the back of the shirt.
3. **Lower ink load.** Reduce top-color opacity to 90% for large flat areas.
4. **Cure exactly to spec.** Under-cured is cracky, over-cured is stiff. Check your press temp weekly.
5. **Match pattern to fabric.** See our LPI cheatsheet — the right pattern for the weave holds up wash after wash.

### Bonus: the wash test
Wash-and-dry 3 times before shipping bulk. If the print softens instead of cracking, you’re in the sweet spot.
`,
};

function ArticleCard({ a }) {
  return (
    <Link to={`/learn/${a.slug}`} className="card-hover group rounded-2xl border border-border bg-card p-6">
      <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><a.icon className="h-5 w-5" /></div>
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{a.tag}</div>
      <h3 className="display-font mt-1 text-lg font-bold">{a.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{a.excerpt}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#4aa4ff]">Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></div>
    </Link>
  );
}

export function LearnHubPage() {
  return (
    <div className="grain">
      <Navbar />
      <section className="relative py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <span className="chip"><BookOpen className="h-3 w-3 text-[#4aa4ff]" /> Learn</span>
          <h1 className="display-font mt-3 text-4xl font-bold sm:text-5xl">Prepress knowledge, free forever.</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">Cheat sheets and playbooks from print operators who’ve been doing this for 20 years. Bookmark this page.</p>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {ARTICLES.map((a) => <ArticleCard key={a.slug} a={a} />)}
          </div>

          <div className="mt-14 rounded-2xl chrome-border p-8 text-center">
            <Flame className="mx-auto h-6 w-6 text-[#4aa4ff]" />
            <h3 className="display-font mt-2 text-xl font-bold">Want a topic covered?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Email us and we’ll add it to the queue.</p>
            <a href="mailto:hello@furnacedesign.lab" className="mt-4 inline-block text-sm text-[#4aa4ff] hover:underline">hello@furnacedesign.lab</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function ArticlePage() {
  const slug = window.location.pathname.split("/learn/")[1] || "";
  const article = ARTICLES.find((a) => a.slug === slug);
  const body = ARTICLE_BODY[slug] || "";
  if (!article) {
    return (
      <div className="grain">
        <Navbar />
        <section className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="display-font text-3xl font-bold">Article not found</h1>
          <Link to="/learn" className="mt-4 inline-block text-[#4aa4ff]">Back to Learn hub</Link>
        </section>
        <Footer />
      </div>
    );
  }
  return (
    <div className="grain">
      <Navbar />
      <article className="relative py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
          <Link to="/learn" className="mono text-[11px] uppercase tracking-widest text-[#4aa4ff] hover:underline">← All articles</Link>
          <div className="mt-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">{article.tag}</div>
          <h1 className="display-font mt-2 text-4xl font-bold sm:text-5xl">{article.title}</h1>
          <div className="prose prose-invert mt-8 max-w-none text-muted-foreground [&_h3]:mt-6 [&_h3]:display-font [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-foreground [&_p]:mt-3 [&_p]:leading-relaxed [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_ol]:mt-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1 [&_blockquote]:mt-4 [&_blockquote]:border-l-2 [&_blockquote]:border-[#4aa4ff]/60 [&_blockquote]:pl-4 [&_blockquote]:italic [&_table]:w-full [&_th]:text-left [&_th]:py-2 [&_th]:px-3 [&_th]:border-b [&_th]:border-border [&_td]:py-2 [&_td]:px-3 [&_td]:border-b [&_td]:border-border/60">
            {body.split("\n").map((line, i) => renderLine(line, i))}
          </div>
          <div className="mt-12 rounded-2xl border border-border bg-card p-6">
            <h3 className="display-font text-lg font-bold">Ready to try it?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Open the Halftone Studio and put this into practice — 3 free exports this month.</p>
            <Link to="/studio/halftone"><span className="mt-3 inline-block rounded-md bg-[#4aa4ff] px-4 py-2 text-sm font-semibold text-[#05070d]">Open Halftone Studio</span></Link>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
}

function renderLine(line, key) {
  if (!line.trim()) return <div key={key} className="h-2" />;
  if (line.startsWith("### ")) return <h3 key={key}>{line.slice(4)}</h3>;
  if (line.startsWith("> ")) return <blockquote key={key}>{line.slice(2)}</blockquote>;
  if (line.startsWith("- ")) return <div key={key} className="ml-4">• {inlineMd(line.slice(2))}</div>;
  if (line.match(/^\d+\.\s/)) return <div key={key} className="ml-4">{inlineMd(line)}</div>;
  if (line.startsWith("|")) return renderTableRow(line, key);
  return <p key={key}>{inlineMd(line)}</p>;
}
function inlineMd(txt) {
  const parts = txt.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") ? <strong key={i} className="text-foreground">{p.slice(2, -2)}</strong> : p);
}
function renderTableRow(line, key) {
  const cells = line.split("|").slice(1, -1).map((c) => c.trim());
  const isSep = cells.every((c) => /^-+$/.test(c));
  if (isSep) return null;
  return (
    <div key={key} className="grid grid-cols-3 gap-2 border-b border-border/60 py-1 text-sm">
      {cells.slice(0, 3).map((c, i) => <div key={i} className={i === 0 ? "text-foreground/90" : "text-muted-foreground"}>{c}</div>)}
    </div>
  );
}
